import React, { useEffect, useState, useMemo } from 'react';
import { Provider, useSelector } from 'react-redux';
import { PaperProvider } from 'react-native-paper';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider } from '@coffeeplexity/ui';
import { AuthProvider } from '@coffeeplexity/shared';
import { NavigationContainer } from '@react-navigation/native';
import {
  DefaultTheme as NavigationDefaultTheme,
  DarkTheme as NavigationDarkTheme,
} from '@react-navigation/native';
import { store, persistor } from '../store';
import { AIProvider } from './AIProvider';
import { useColorScheme, View, ActivityIndicator, Text } from 'react-native';
import { queryClient, asyncStoragePersister } from '../config/queryClient';
import { selectUserPreferences } from '../store/slices/userSlice';
import { lightTheme, darkTheme } from '../theme';
import { initializationService } from '../services/InitializationService';
import { logger } from '../utils/logger';
import { linking } from '../config/linking';
import { themeStorage, ThemePreference } from '../utils/themeStorage';

interface AppProvidersProps {
  children: React.ReactNode;
}

/**
 * ThemedAppContent - Inner component that has access to Redux store
 * Handles theme selection based on user preferences
 */
function ThemedAppContent({ children }: AppProvidersProps) {
  const systemColorScheme = useColorScheme();
  const userPreferences = useSelector(selectUserPreferences);
  const [localThemePreference, setLocalThemePreference] =
    useState<ThemePreference>('auto');
  const [isThemeLoaded, setIsThemeLoaded] = useState(false);

  // Load theme preference from local storage on mount
  useEffect(() => {
    const loadThemePreference = async () => {
      const stored = await themeStorage.getThemePreference();
      setLocalThemePreference(stored);
      setIsThemeLoaded(true);
    };
    loadThemePreference();
  }, []);

  // Sync local storage with user preferences when logged in
  useEffect(() => {
    const syncThemePreference = async () => {
      if (userPreferences?.theme) {
        // User is logged in, use their preference
        if (userPreferences.theme !== localThemePreference) {
          setLocalThemePreference(userPreferences.theme);
          await themeStorage.setThemePreference(userPreferences.theme);
        }
      }
    };
    syncThemePreference();
  }, [userPreferences?.theme]);

  // Determine theme based on local preference (which may come from user prefs or local storage)
  const themePreference = localThemePreference;

  const isDark =
    themePreference === 'dark' ||
    (themePreference === 'auto' && systemColorScheme === 'dark');

  const selectedTheme = isDark ? darkTheme : lightTheme;

  // Create navigation theme that matches Paper theme colors
  const navigationTheme = useMemo(
    () => ({
      ...(isDark ? NavigationDarkTheme : NavigationDefaultTheme),
      colors: {
        ...(isDark
          ? NavigationDarkTheme.colors
          : NavigationDefaultTheme.colors),
        background: selectedTheme.colors.background,
        card: selectedTheme.colors.surface,
        text: selectedTheme.colors.onSurface,
        border: selectedTheme.colors.outline,
        primary: selectedTheme.colors.primary,
        notification: selectedTheme.colors.error,
      },
    }),
    [isDark, selectedTheme]
  );

  // Show loading state while theme is being loaded
  if (!isThemeLoaded) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size='large' />
      </View>
    );
  }

  return (
    <PaperProvider theme={selectedTheme}>
      <AuthProvider>
        <ThemeProvider initialMode={themePreference}>
          <SafeAreaProvider>
            <GestureHandlerRootView style={{ flex: 1 }}>
              <NavigationContainer theme={navigationTheme} linking={linking}>
                <AIProvider>{children}</AIProvider>
              </NavigationContainer>
            </GestureHandlerRootView>
          </SafeAreaProvider>
        </ThemeProvider>
      </AuthProvider>
    </PaperProvider>
  );
}

/**
 * AppProviders - Comprehensive Provider Setup
 *
 * Provides all necessary context providers for the CoffeePlexity mobile app:
 * - Redux store for state management with persistence
 * - React Query client for server state management
 * - React Native Paper theme provider
 * - Safe area context for proper screen handling
 * - Gesture handler for touch interactions
 * - AI services provider for GraphQL and AI features
 * - Database initialization for offline support
 *
 * Features:
 * - User-controlled theme selection (light/dark/auto) with local persistence
 * - Query persistence for offline support
 * - Redux state persistence for user preferences and auth
 * - Database initialization for offline journey support
 * - Proper TypeScript typing
 * - Error boundary integration ready
 * - Performance optimized with proper provider nesting
 */
export function AppProviders({ children }: AppProvidersProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        logger.debug('Initializing app services...');
        await initializationService.initialize();
        setIsInitialized(true);
        logger.debug('App services initialized successfully');
      } catch (error) {
        logger.error('Failed to initialize app services:', error);
        setInitError(error instanceof Error ? error.message : 'Unknown error');
        // Still set initialized to true to allow app to run without offline features
        setIsInitialized(true);
      }
    };

    initializeApp();
  }, []);

  if (!isInitialized) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size='large' />
        <Text style={{ marginTop: 16 }}>Initializing app...</Text>
      </View>
    );
  }

  if (initError) {
    logger.warn('App running with limited offline support:', initError);
  }

  return (
    <Provider store={store}>
      <PersistGate
        loading={
          <View
            style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}
          >
            <ActivityIndicator size='large' />
          </View>
        }
        persistor={persistor}
      >
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{ persister: asyncStoragePersister }}
        >
          <ThemedAppContent>{children}</ThemedAppContent>
        </PersistQueryClientProvider>
      </PersistGate>
    </Provider>
  );
}
